/**
 * Cache utilities for memoizing function results
 * Copied from 3in1CMS: bajajfinserv-web/ui.solid/src/lib/util/cache.ts
 */

const cache = new WeakMap();

/**
 * Creates a caching mechanism for a synchronous function using WeakMap.
 *
 * @template Fn - The type of the function to be cached.
 *
 * @param {string} key - The key for the cache entry.
 * @param {Fn} fn - The function to be cached.
 * @param {...Parameters<Fn>} args - The arguments to be passed to the function.
 *
 * @returns - The result of the function, either from cache or by calling the function.
 */
export const createCache = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Fn extends (...args: any[]) => any,
>(
  key: string,
  fn: Fn,
  ...args: Parameters<Fn>
): ReturnType<Fn> => {
  let cachedResults = cache.get(fn);
  if (!cachedResults) {
    cachedResults = {};
    cache.set(fn, cachedResults);
  }
  if (!cachedResults[key]) {
    const result = fn(...args);
    cachedResults[key] = result;
  }
  return cachedResults[key];
};

/**
 * Creates a cache entry for an asynchronous function using a WeakMap.
 *
 * @template Fn - The type of the asynchronous function to be cached.
 *
 * @param {string} key - The key for the cache entry.
 * @param {Fn} fn - The asynchronous function to be cached.
 * @param {...Parameters<Fn>} args - The arguments to be passed to the asynchronous function.
 *
 * @returns - A promise that resolves to the result of the asynchronous function,
 * either from cache or by calling the function.
 */
export const createCacheAsync = async <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Fn extends (...args: any[]) => any,
>(
  key: string,
  fn: Fn,
  ...args: Parameters<Fn>
): Promise<ReturnType<Fn>> => {
  let cachedResults = cache.get(fn);
  if (!cachedResults) {
    cachedResults = {};
    cache.set(fn, cachedResults);
  }
  if (!cachedResults[key]) {
    const result = fn(...args).catch(() => {
      cachedResults[key] = undefined;
    });
    cachedResults[key] = result;
  }
  return cachedResults[key];
};

/**
 * Clears the cache for a specific key and function combination.
 *
 * @template Fn - The type of the function.
 * @param {string} key - The cache key to clear.
 * @param {Fn} fn - The function whose cache entry should be cleared.
 */
export const clearCacheForKey = <Fn extends (...args: Parameters<Fn>) => ReturnType<Fn>>(
  key: string,
  fn: Fn
): void => {
  const cachedResults = cache.get(fn);
  if (cachedResults) {
    cachedResults[key] = undefined;
  }
};

/**
 * Clears all cached results for a specific function.
 *
 * @template Fn - The type of the function.
 * @param {Fn} fn - The function whose entire cache should be cleared.
 */
export const clearCacheForFunction = <Fn extends (...args: Parameters<Fn>) => ReturnType<Fn>>(
  fn: Fn
): void => {
  cache.delete(fn);
};
